"use client";

import { useState, useRef, useEffect } from "react";
import { Play, Pause } from "lucide-react";
import { useNavigate } from "react-router-dom";

const videos = [
  {
    id: "video-1",
    url: "https://d1twcv82c6wl0s.cloudfront.net/video-1.mp4",
    title: "Medieval Battle",
    thumbnail: "https://d1twcv82c6wl0s.cloudfront.net/image-1.png",
    videoUrlName: "video-1.mp4",
  },
  {
    id: "video-2",
    url: "https://d1twcv82c6wl0s.cloudfront.net/video-2.mp4",
    title: "Family Man",
    thumbnail: "https://d1twcv82c6wl0s.cloudfront.net/image-2.png",
    videoUrlName: "video-2.mp4",
  },
  {
    id: "video-3",
    url: "https://d1twcv82c6wl0s.cloudfront.net/video-3.mp4",
    title: "Robots vs Robots",
    thumbnail: "https://d1twcv82c6wl0s.cloudfront.net/image-3.png",
    videoUrlName: "video-3.mp4",
  },
  {
    id: "video-4",
    url: "https://d1twcv82c6wl0s.cloudfront.net/video-4.mp4",
    title: "Gangsters Busted",
    thumbnail: "https://d1twcv82c6wl0s.cloudfront.net/image-4.png",
    videoUrlName: "video-4.mp4",
  },
  {
    id: "video-5",
    url: "https://d1twcv82c6wl0s.cloudfront.net/video-5.mp4",
    title: "Space Wars",
    thumbnail: "https://d1twcv82c6wl0s.cloudfront.net/image-5.png",
    videoUrlName: "video-5.mp4",
  },
];

const VideoThumbnail = ({ video, onClick, isActive }) => {
  return (
    <div onClick={onClick} className="relative cursor-pointer group">
      <div
        className="relative overflow-hidden rounded-lg video-shadow"
        style={{
          border: "2px solid #75388d",
        }}
      >
        <img
          src={video.thumbnail}
          alt={video.title}
          className="w-full h-full aspect-video object-cover transition-transform duration-300 group-hover:scale-105 bg-gray-900"
        />
        <div
          className={`absolute inset-0 ${
            isActive
              ? "bg-purple-500/40"
              : "bg-purple-500/20 group-hover:bg-purple-500/30"
          } transition-colors`}
        />
      </div>
      <p className="mt-2 text-white text-center">{video.title}</p>
    </div>
  );
};

export default function Home() {
  const navigate = useNavigate();
  const [activeVideo, setActiveVideo] = useState(videos[0]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processStatus, setProcessStatus] = useState("");
  const [processTimestamp, setProcessTimestamp] = useState("");

  const mainVideoRef = useRef(null);

  useEffect(() => {
    const videoElement = mainVideoRef.current;
    if (videoElement) {
      videoElement.pause();
      videoElement.load();
      setIsPlaying(false);
    }
  }, [activeVideo]);

  useEffect(() => {
    const videoElement = mainVideoRef.current;
    if (!videoElement) return;

    const handleEnded = () => {
      setIsPlaying(false);
    };

    const handleError = (e) => {
      console.error("Video error:", e);
      setIsPlaying(false);
    };

    videoElement.addEventListener("ended", handleEnded);
    videoElement.addEventListener("error", handleError);

    return () => {
      videoElement.removeEventListener("ended", handleEnded);
      videoElement.removeEventListener("error", handleError);
    };
  }, []);

  const handleVideoClick = (video) => {
    if (video.id !== activeVideo.id) {
      setActiveVideo(video);
    }
  };

  const togglePlayPause = async () => {
    const videoElement = mainVideoRef.current;
    if (!videoElement) return;

    try {
      if (isPlaying) {
        videoElement.pause();
        setIsPlaying(false);
      } else {
        await videoElement.play();
        setIsPlaying(true);
      }
    } catch (error) {
      console.error("Error toggling play/pause:", error);
      setIsPlaying(false);
    }
  };

  const handleProcess = async () => {
    setIsProcessing(true);
    setProcessStatus("Processing...");
    const startTime = Date.now();
  
    // Utility function to update process status with elapsed time
    const updateStatus = (message) => {
      const elapsed = Math.round((Date.now() - startTime) / 1000);
      setProcessStatus(`${message}`);
    };
  
    const delay = (seconds) =>
      new Promise((resolve) => setTimeout(resolve, seconds * 1000));
  
    const baseUrl = "https://mediasentinel-api.cloudmates.me";
    const apiUrl = `${baseUrl}/videos/${activeVideo.videoUrlName}/process`;
  
    try {
      // Sequentially update statuses with delays
      await delay(5);
      updateStatus("Fetching video content...");
  
      await delay(5);
      updateStatus("Initializing AI engines...");
  
      await delay(10);
      updateStatus("Scanning video for sensitive scenes...");
  
      await delay(10);
      updateStatus("Compiling results..");
  
      // Make API call
      const response = await fetch(apiUrl, { method: "POST" });
  
      if (!response.ok) {
        throw new Error(`API call failed with status: ${response.status}`);
      }
  
      const data = await response.json();
      console.log("Processing result:", data);
  
      // Save video and response to localStorage
      localStorage.setItem("processingVideo", JSON.stringify(activeVideo));
      localStorage.setItem("processingVideoResponse", JSON.stringify(data));
  
      // Update status for result compilation
      updateStatus("Compiling results...");
      await delay(5);
  
      setProcessStatus("Processing complete. Results saved!");
      navigate("/ai/process");
    } catch (error) {
      console.error("Error processing video:", error);
      setProcessStatus(
        "Processing failed. Please try again or contact support if the issue persists."
      );
    } finally {
      setIsProcessing(false);
    }
  };
  

  return (
    <main className="min-h-screen w-full relative">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat -z-10"
        style={{
          backgroundImage: "url(../assets/images/home-bg.png)",
        }}
      />
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-8 heading-text">
          Media Sentinel
        </h1>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          {videos.map((video) => (
            <VideoThumbnail
              key={video.id}
              video={video}
              onClick={() => handleVideoClick(video)}
              isActive={video.id === activeVideo.id}
            />
          ))}
        </div>

        <div className="grid md:grid-cols-3 gap-8 ">
          {/* Left side - Video Player */}
          <div className="col-span-2">
            <div className="relative aspect-video w-full rounded-lg overflow-hidden bg-black/80">
              <video
                ref={mainVideoRef}
                className="absolute inset-0 w-full h-full object-cover"
                playsInline
              >
                <source src={activeVideo.url} type="video/mp4" />
                Your browser does not support the video tag.
              </video>

              <button
                onClick={togglePlayPause}
                className={`absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${
                  isPlaying
                    ? "opacity-0 hover:opacity-100 bg-black/10 hover:bg-black/30"
                    : "bg-black/30"
                } group`}
              >
                <div className="rounded-full p-4 bg-purple-600/80 group-hover:bg-purple-600 transition-colors">
                  {isPlaying ? (
                    <Pause className="w-12 h-12 text-white" />
                  ) : (
                    <Play className="w-12 h-12 text-white" />
                  )}
                </div>
              </button>

              <div className="absolute inset-0 pointer-events-none opacity-50 bg-gradient-to-t from-purple-500/20 via-transparent to-transparent" />
            </div>

            {/* Display current video thumbnail and title */}
            <div className="mt-4 flex items-center gap-4 justify-center">
              <h3 className="text-white text-xl">{activeVideo.title}</h3>
            </div>
          </div>
          <div className="flex justify-center items-center">
  <button
    onClick={handleProcess}
    className="px-10 py-3 text-white rounded-lg font-medium relative group overflow-hidden button-shadow"
    disabled={isProcessing} // Disable button while processing
  >
    <span className="relative z-10 text-shadow">
      {isProcessing
        ? processStatus // Show dynamic process status during processing
        : "Initiate AI Analysis"}
    </span>
  </button>
</div>

        </div>

        {/* Process Status */}
        {/* {processStatus && (
          <div className="mt-4 text-center text-white">{processStatus}</div>
        )} */}
      </div>
    </main>
  );
}
